import clsx from 'clsx';

import { AppliedPolicyStatusType } from '@/react/portainer/environments/types';

/** Props for the PolicyStatusBadge component */
export interface PolicyStatusBadgeProps {
  /** Status enum value */
  status: AppliedPolicyStatusType;
  /** Number of environments with this status */
  count: number;
  /** Display label for the status */
  label: string;
  /** Whether this badge is currently selected */
  isSelected: boolean;
  /** Callback when badge is clicked */
  onClick(status: AppliedPolicyStatusType): void;
  /** Radio group name */
  name: string;
}

/**
 * Color configuration for each status type.
 * Maps status types to their visual styling for the dot indicator and selection underline.
 */
type StatusColorConfig = {
  /** Tailwind color class for the status dot indicator */
  dotColor: string;
  /** Tailwind color class for the underline when selected */
  underlineColor: string;
};

const statusColors: Record<AppliedPolicyStatusType, StatusColorConfig> = {
  [AppliedPolicyStatusType.Applied]: {
    dotColor: 'bg-success-7',
    underlineColor: 'bg-success-7',
  },
  [AppliedPolicyStatusType.Warning]: {
    dotColor: 'bg-warning-7',
    underlineColor: 'bg-warning-7',
  },
  [AppliedPolicyStatusType.Failed]: {
    dotColor: 'bg-error-7',
    underlineColor: 'bg-error-7',
  },
  [AppliedPolicyStatusType.InProgress]: {
    dotColor: 'bg-blue-7',
    underlineColor: 'bg-blue-7',
  },
  [AppliedPolicyStatusType.NotSupported]: {
    dotColor: 'bg-gray-7',
    underlineColor: 'bg-gray-7',
  },
};

/**
 * A clickable filter button for filtering policy attachments by status.
 * Features a colored dot indicator, clear text, and an underline on selection.
 * Supports both light and dark themes via Tailwind/Portainer conventions.
 */
export function PolicyStatusBadge({
  status,
  count,
  label,
  isSelected,
  onClick,
  name,
}: PolicyStatusBadgeProps) {
  // Don't render badge if count is 0
  if (count === 0) {
    return null;
  }

  const colors = statusColors[status];

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Label wraps radio input and provides custom toggle behavior with full keyboard support
    <label
      className={clsx(
        // Layout and spacing
        'relative mb-0 flex items-center gap-2 px-3 py-2',
        // Reset button styles / Label as button
        'cursor-pointer border-0 bg-transparent',
        // Text styling - neutral color that works in both themes
        'text-sm font-medium',
        'text-gray-9 th-highcontrast:text-gray-3 th-dark:text-gray-3',
        // Hover effect
        'hover:bg-gray-3 th-highcontrast:hover:bg-gray-10 th-dark:hover:bg-gray-10',
        'rounded-md transition-colors duration-150',
        // Focus styles for accessibility (applied to the label when input is focused)
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-5 has-[:focus-visible]:ring-offset-1',
        // Selected state background
        isSelected && 'bg-gray-3 th-highcontrast:bg-gray-10 th-dark:bg-gray-10'
      )}
      title={isSelected ? 'Clear filter' : `Filter by ${label}`}
      data-cy={`status-badge-${status}`}
      onClick={(e) => {
        // Handle click on label for toggle behavior
        // This allows clicking an already-selected radio to deselect it
        e.preventDefault();
        onClick(status);
      }}
      onKeyDown={(e) => {
        // Handle keyboard activation (Space or Enter)
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onClick(status);
        }
      }}
    >
      <input
        type="radio"
        className="sr-only"
        name={name}
        value={status.toString()}
        checked={isSelected}
        onChange={() => {}}
        aria-label={`Filter by ${label}`}
        tabIndex={0}
      />
      {/* Status dot indicator */}
      <span
        className={clsx('h-2 w-2 shrink-0 rounded-full', colors.dotColor)}
        aria-hidden="true"
      />

      {/* Count and label */}
      <span>
        {count} {label}
      </span>

      {/* Underline indicator for selected state */}
      {isSelected && (
        <span
          className={clsx(
            'absolute bottom-0 left-3 right-3 h-0.5 rounded-full',
            colors.underlineColor
          )}
          aria-hidden="true"
        />
      )}
    </label>
  );
}
